/**
 * Server state of «Зафиксировать»: the reference lists, the debounced dry-run preview and the save
 * (decisions-log V29).
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CaptureEventInput,
  CaptureLookups,
  CapturePreviewResponse,
  CaptureResponse,
} from '@outsource/shared';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';

export const captureKeys = {
  lookups: ['capture-lookups'] as const,
  preview: (event: string | null) => ['capture-preview', event] as const,
};

/** Debounce of the preview dry run. */
const PREVIEW_DEBOUNCE_MS = 200;

/** GET /api/v1/capture/lookups — people, projects, clients, contracts, assignments, entities. */
export function useCaptureLookups() {
  return useQuery({
    queryKey: captureKeys.lookups,
    queryFn: () => api.get<CaptureLookups>('/capture/lookups'),
    staleTime: 10_000,
  });
}

function useDebounced<T>(value: T, ms: number): T {
  const [current, setCurrent] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setCurrent(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return current;
}

/** POST /api/v1/capture/preview for the form's event (null = nothing to preview yet). */
export function useCapturePreview(event: CaptureEventInput | null) {
  const key = useDebounced(event ? JSON.stringify(event) : null, PREVIEW_DEBOUNCE_MS);
  return useQuery({
    queryKey: captureKeys.preview(key),
    queryFn: () => api.post<CapturePreviewResponse>('/capture/preview', { event: JSON.parse(key ?? 'null') }),
    enabled: key !== null,
    placeholderData: keepPreviousData,
    staleTime: 0,
  });
}

/** POST /api/v1/capture; every screen refreshes afterwards. */
export function useCaptureSave() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (event: CaptureEventInput) => api.post<CaptureResponse>('/capture', { event }),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}
